<script setup lang="ts">
  import { UTCDate } from '@date-fns/utc'
  import { useQuery } from '@tanstack/vue-query'
  import { createReusableTemplate, useMagicKeys, watchDebounced, whenever } from '@vueuse/core'
  import { format } from 'date-fns'
  import {
    computed,
    h,
    onMounted,
    reactive,
    ref,
    toRaw,
    useTemplateRef,
    watch,
    watchEffect,
  } from 'vue'
  import { useAuth } from '~renderer/composables/useAuth'
  import { backgroundMap } from '~renderer/util/island'
  import { guard } from '~renderer/util/telefunc'
  import { onCheckIslandUrl } from '~rpc/procedures/island/onCheckIslandUrl.telefunc'
  import { onCreateIsland } from '~rpc/procedures/island/onCreateIsland.telefunc'
  import { onGetIslandsByHostId } from '~rpc/procedures/island/onGetIslandsByHostId.telefunc'
  import { onRehostIsland } from '~rpc/procedures/island/onRehostIsland.telefunc'
  import * as IslandConfig from '~shared/config/island'
  import type { IslandPublicView } from '~shared/types/island'

  import type { CreateIslandRequestData } from '~shared/validation/requests/island.zod'
  import ClampedIntegerInput from './ClampedIntegerInput.vue'
  import IslandCard from './IslandCard.vue'
  import TurnipExchangeDropdown from './TurnipExchangeDropdown.vue'

  const emit = defineEmits(['success', 'cancel'])
  const [DefineHeader, Header] = createReusableTemplate()
  const [DefineStepIndicator, StepIndicator] = createReusableTemplate()
  const [DefineStep, Step] = createReusableTemplate<
    {
      hideIndicator?: boolean
    },
    Record<'default' | 'header', undefined>
  >()
  const rootEl = useTemplateRef('rootEl')
  const { user, isAnonymous, hasPatreonTier } = useAuth()
  const { Enter } = useMagicKeys()

  const currentStep = ref(0)
  const chosenIslandId = ref<string | undefined>()
  const rehostWithEdits = ref(false)
  const isCheckingUrlCode = ref(false)
  const islandTime = ref(format(new Date(), "yyyy-MM-dd'T'HH:mm"))
  const error = ref<string>()
  const loading = ref(false)
  const entryFeeData = reactive({
    bells: 0,
    nmt: 0,
  })
  const createFormDefault = () => ({
    name: '',
    dodoCode: '',
    type: 'turnips' as IslandConfig.Type,
    turnipPrice: 0,
    fruit: 'apple' as IslandConfig.Fruit,
    hemisphere: 'north' as IslandConfig.Hemisphere,
    description: '',
    islandLimit: 1,
    queueLimit: 5,
    npc: [] as IslandConfig.NPC[],
    private: false,
    privacy: [] as IslandConfig.PrivacyOption[],
    customUrlTag: '',
    customBackgroundId: '' as IslandConfig.CustomBackground,
  })
  const createFormDataFromIsland = (record: IslandPublicView) => {
    const clone = structuredClone(toRaw(record))
    return {
      name: clone.name,
      dodoCode: clone.dodoCode,
      type: clone.type,
      turnipPrice: clone.turnipPrice,
      fruit: clone.fruit,
      hemisphere: clone.hemisphere,
      description: clone.description,
      islandLimit: clone.islandLimit,
      queueLimit: clone.queueLimit,
      npc: clone.npc,
      private: clone.private,
      privacy: clone.privacy,
      customUrlTag: hasPatreonTier(1) ? clone.customUrlTag : '',
      customBackgroundId: hasPatreonTier(1) ? clone.customBackgroundId : '',
    }
  }
  const formData = reactive(
    createFormDefault() satisfies Omit<CreateIslandRequestData, 'islandTime' | 'entryFee'>,
  )

  const { data } = useQuery({
    queryKey: ['own-islands', user.value?.id],
    queryFn: async () => {
      if (!user.value?.id) return []

      const islands = await guard(
        onGetIslandsByHostId({
          hostId: user.value?.id,
        }),
      )
      return islands
    },
    staleTime: Infinity,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })

  const { data: urlCodeAvailable, refetch: checkUrlCode } = useQuery({
    queryKey: ['url-code-check', formData.customUrlTag],
    queryFn: async () => {
      if (!formData.customUrlTag) return false
      isCheckingUrlCode.value = true
      const isAvailable = await guard(
        onCheckIslandUrl({
          urlCode: formData.customUrlTag,
          islandId: chosenIsland.value?.islandId || undefined,
        }),
      )
      isCheckingUrlCode.value = false
      return isAvailable
    },
    staleTime: Infinity,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  })

  const closedIslands = computed(() => data.value?.filter((island) => island.status === 'closed'))
  const activeIslands = computed(() => data.value?.filter((island) => island.status !== 'closed'))

  const userIslandLimit = computed(() => (hasPatreonTier(1) ? 9 : 1))
  const canHostIsland = computed(
    () =>
      user.value &&
      activeIslands.value != null &&
      activeIslands.value.length < userIslandLimit.value,
  )

  const islandDropdownList = computed(
    () =>
      closedIslands.value?.map((island) => {
        return {
          value: island.islandId,
          label: `${island.name}`,
        }
      }) ?? [],
  )

  const chosenIslandItem = computed(() =>
    islandDropdownList.value?.find((item) => item.value === chosenIslandId.value),
  )
  const chosenIsland = computed(() => {
    const foundIsland = structuredClone(
      toRaw(closedIslands.value?.find((island) => island.islandId === chosenIslandId.value)),
    )
    if (foundIsland && !hasPatreonTier(1)) {
      foundIsland.customBackgroundId = ''
      foundIsland.customUrlTag = ''
    }
    return foundIsland
  })

  // elaborated step count method strucutred for future-proofing
  const stepCount = computed(() => {
    let numberOfSteps = 14
    //if (closedIslands.value?.length) numberOfSteps++
    if (hasPatreonTier(1)) numberOfSteps++
    return numberOfSteps
  })

  const stepIndication = computed(() =>
    closedIslands.value?.length ? currentStep.value + 1 : currentStep.value,
  )

  const onLeaveWizardClicked = () => {
    emit('cancel')
  }

  const onHostIslandClicked = () => {
    if (!chosenIsland.value) return
    Object.assign(formData, createFormDataFromIsland(chosenIsland.value))
    const bellsFeeItem = chosenIsland.value.entryFee?.find((fee) => fee[0]?.type === 'bells')?.[0]
    entryFeeData.bells = bellsFeeItem && 'amount' in bellsFeeItem ? bellsFeeItem.amount : 0
    const nmtFeeItem = chosenIsland.value.entryFee?.find((fee) => fee[0]?.type === 'nmt')?.[0]
    entryFeeData.nmt = nmtFeeItem && 'amount' in nmtFeeItem ? nmtFeeItem.amount : 0
    currentStep.value = 2 // Dodo Code
    rehostWithEdits.value = false
  }

  const onHostEditClicked = () => {
    if (!chosenIsland.value) return
    Object.assign(formData, createFormDataFromIsland(chosenIsland.value))
    const bellsFeeItem = chosenIsland.value.entryFee?.find((fee) => fee[0]?.type === 'bells')?.[0]
    entryFeeData.bells = bellsFeeItem && 'amount' in bellsFeeItem ? bellsFeeItem.amount : 0
    const nmtFeeItem = chosenIsland.value.entryFee?.find((fee) => fee[0]?.type === 'nmt')?.[0]
    entryFeeData.nmt = nmtFeeItem && 'amount' in nmtFeeItem ? nmtFeeItem.amount : 0
    currentStep.value = 1
    rehostWithEdits.value = true
  }

  const onHostNewClicked = () => {
    Object.assign(formData, createFormDefault())
    entryFeeData.bells = 0
    entryFeeData.nmt = 0
    chosenIslandId.value = undefined
    currentStep.value = 1
    rehostWithEdits.value = false
  }

  const nextStep = () => {
    if (!rehostWithEdits.value && chosenIslandId.value) {
      if (currentStep.value == 2 && canProceed.value) currentStep.value = stepCount.value - 1
      return
    }

    if (canProceed.value && !onLastStep.value) currentStep.value++
  }

  const prevStep = () => {
    if (!rehostWithEdits.value && chosenIslandId.value) {
      if (currentStep.value == 2) currentStep.value = 0
      if (onLastStep.value) currentStep.value = 2
      return
    }

    if (currentStep.value > 0) currentStep.value--
    if (currentStep.value === 0 && !chosenIslandId.value && closedIslands.value?.length)
      chosenIslandId.value = closedIslands.value[0]!.islandId ?? ''
  }

  const submit = async () => {
    error.value = undefined
    loading.value = true

    try {
      const { bells, nmt } = entryFeeData
      const entryFee = [
        bells > 0 ? [{ type: 'bells', amount: bells } as const] : null,
        nmt > 0 ? [{ type: 'nmt', amount: nmt } as const] : null,
      ].filter((fee) => !!fee)

      let turnipCode: string | undefined | null
      const islandData = toRaw(formData)
      if (chosenIslandId.value) {
        const rehostResult = await guard(
          onRehostIsland({
            islandId: chosenIslandId.value,
            updateIslandRequestData: {
              ...islandData,
              // Only include entry fee if there is one
              ...(entryFee.length > 0 && { entryFee }),
              // Force to UTC Time, all island times are standardized to UTC
              islandTime: new UTCDate(`${islandTime.value}Z`),
            },
          }),
        )

        turnipCode = rehostResult.customUrlTag
          ? `@${rehostResult.customUrlTag}`
          : rehostResult.turnipCode

        emit('success')
      } else {
        const createResult = await guard(
          onCreateIsland({
            createIslandRequestData: {
              ...islandData,
              // Only include entry fee if there is one
              ...(entryFee.length > 0 && { entryFee }),
              // Force to UTC Time, all island times are standardized to UTC
              islandTime: new UTCDate(`${islandTime.value}Z`),
            },
          }),
        )

        turnipCode = createResult.customUrlTag
          ? `@${createResult.customUrlTag}`
          : createResult.turnipCode

        emit('success')
      }

      if (!turnipCode) throw new Error('Failed to get turnip code')

      // TODO: Maybe a modal here with a button to take them?
      window.location.href = `/island/${turnipCode}`
    } catch (e: any) {
      error.value = e.message
    } finally {
      loading.value = false
    }
  }

  const TurnipIcon = () => h('img', { src: '/images/icons/turnip.svg', class: 'size-6' })

  // Options
  const islandTypes: Array<{ name: string; value: IslandConfig.Type }> = [
    { name: 'Turnips', value: 'turnips' },
    { name: 'Treasure', value: 'treasure' },
    { name: 'Crafting', value: 'crafting' },
    { name: 'Event', value: 'event' },
    { name: 'Catalog', value: 'catalog' },
  ]
  const fruits: Array<{ name: string; icon: string; value: IslandConfig.Fruit }> = [
    { name: 'Apple', icon: '/images/icons/apple.svg', value: 'apple' },
    { name: 'Cherry', icon: '/images/icons/cherry.svg', value: 'cherry' },
    { name: 'Orange', icon: '/images/icons/orange.svg', value: 'orange' },
    { name: 'Peach', icon: '/images/icons/peach.svg', value: 'peach' },
    { name: 'Pear', icon: '/images/icons/pear.svg', value: 'pear' },
    { name: 'Coconut', icon: '/images/icons/coconut.svg', value: 'coconut' },
  ]
  const backgrounds: Array<{ label: string; icon: string; value: IslandConfig.CustomBackground }> =
    [
      { label: 'Default', icon: '', value: '' },
      { label: 'Bones (Dark)', icon: '/images/backgrounds/bones-dark.png', value: 'bones-dark' },
      {
        label: 'Butterflies (Dark)',
        icon: '/images/backgrounds/butterflies-dark.png',
        value: 'butterflies-dark',
      },
      { label: 'Coins (Dark)', icon: '/images/backgrounds/coins-dark.png', value: 'coins-dark' },
      { label: 'Fish (Dark)', icon: '/images/backgrounds/fish-dark.png', value: 'fish-dark' },
      { label: 'Fruits (Dark)', icon: '/images/backgrounds/fruits-dark.png', value: 'fruits-dark' },
      { label: 'Leafs (Dark)', icon: '/images/backgrounds/leafs-dark.png', value: 'leafs-dark' },
      //{ label: 'Nookazon Exchange (Dark)', value: 'nookazon-exchange-dark' },
      //{ label: 'NUPS (Dark)', value: 'nups-dark' },
      {
        label: 'Spiders (Dark)',
        icon: '/images/backgrounds/spiders-dark.png',
        value: 'spiders-dark',
      },
    ]

  // const entryFees = [
  //   { name: 'Bells', icon: '/images/icons/bells.svg', value: 'Bells' },
  //   { name: 'NMT', icon: '/images/icons/nmt.svg', value: 'NMT' },
  //   { name: 'Furniture', icon: '/images/icons/leaf.svg', value: 'Furniture' },
  //   { name: 'DIY', icon: '/images/icons/diy.svg', value: 'DIY' },
  //   { name: 'None', icon: '', value: 'None' },
  // ]

  const npcs: Array<{ name: string; icon: string; value: IslandConfig.NPC }> = [
    { name: 'Celeste', icon: '/images/npc/celeste.svg', value: 'Celeste' },
    { name: 'Saharah', icon: '/images/npc/saharah.svg', value: 'Saharah' },
    { name: 'Redd', icon: '/images/npc/redd.svg', value: 'Redd' },
    { name: 'Flick', icon: '/images/npc/flick.svg', value: 'Flick' },
    { name: 'C.J.', icon: '/images/npc/cj.svg', value: 'C.J.' },
    { name: 'Leif', icon: '/images/npc/leif.svg', value: 'Leif' },
    { name: 'Kicks', icon: '/images/npc/kicks.svg', value: 'Kicks' },
    { name: 'Wisp', icon: '/images/npc/wisp.svg', value: 'Wisp' },
  ]

  const toggleNpc = (npc?: IslandConfig.NPC) => {
    if (!npc) {
      formData.npc = []
      return
    }

    const isValidNpc = npcs.some((n) => n.value === npc)
    const index = formData.npc.indexOf(npc)

    if (index === -1 && isValidNpc) {
      formData.npc.push(npc)
      return
    }

    formData.npc.splice(index, 1)
  }

  const privacyOptions: Array<{ name: string; icon: string; value: IslandConfig.PrivacyOption }> = [
    { name: 'Twitch Sub', icon: 'fa7-brands-twitch', value: 'twitch-sub' },
    { name: 'Discord Only', icon: 'fa7-brands-discord', value: 'discord' },
    { name: 'Patreon Only', icon: 'fa7-brands-patreon', value: 'patreon' },
  ]

  const togglePrivacy = (privacyOption?: IslandConfig.PrivacyOption) => {
    if (!privacyOption) {
      formData.privacy = []
      return
    }

    const index = formData.privacy.indexOf(privacyOption)

    if (index === -1) {
      formData.privacy.push(privacyOption)
      return
    }

    formData.privacy.splice(index, 1)
  }

  // Valid Dodo Code characters: A-H, J-N, P-Y, 0-9 (excludes I, O, Z)
  const DODO_CODE_VALID = /^[A-HJ-NP-Y0-9]{5}$/i
  const DODO_DIGITS_VALID = /^[A-HJ-NP-Y0-9]{0,5}$/i
  const CUSTOM_CODE_VALID = /^[a-z0-9_-]{3,20}$/

  const feeDescription = computed(() => {
    const { bells, nmt } = entryFeeData
    const bellsText = `<strong><img class="inline w-4 h-4" src="/images/icons/bells.svg"> ${bells}</strong>`
    const nmtText = `<strong><img class="inline w-4 h-4" src="/images/icons/nmt.svg"> ${nmt}</strong>`

    return bells > 0 && nmt > 0
      ? `either ${bellsText} OR ${nmtText}`
      : bells > 0
        ? bellsText
        : nmt > 0
          ? nmtText
          : 'no entry fee'
  })

  const previewIslandData = computed(() => {
    const { bells, nmt } = entryFeeData
    const entryFee: Array<Array<IslandConfig.EntryFee>> = []
    if (bells > 0) entryFee.push([{ type: 'bells', amount: bells }])
    if (nmt > 0) entryFee.push([{ type: 'nmt', amount: nmt }])

    return {
      ...formData,
      entryFee: entryFee.length > 0 ? entryFee : null,
      islandTime: new UTCDate(`${islandTime.value}Z`),
      hostId: '',
      islandId: '',
      turnipCode: '',
      announcement: '',
      listPriority: 0,
      status: 'open',
      currentVisitorCount: 0,
      currentWaitingCount: 0,
      syncedAt: new Date(),
      announcedAt: new Date(),
      createdAt: new Date(),
      updatedAt: new Date(),
      averageRating: 4,
      totalRatings: 0,
    } satisfies IslandPublicView
  })

  const canProceed = computed(() => {
    switch (currentStep.value) {
      // Rehost existing
      case 0:
        return true
      // Island name
      case 1:
        return formData.name.trim().length >= 3 && formData.name.trim().length <= 10
      // Dodo code
      case 2:
        return formData.dodoCode.length === 5 && DODO_CODE_VALID.test(formData.dodoCode)
      // Island type
      case 3:
        return true
      // Fruit
      case 4:
        return true
      // Hemisphere
      case 5:
        return true
      // Time
      case 6:
        return true
      // NPC
      case 7:
        return formData.npc.length >= 0 && formData.npc.length <= 4
      // Turnip price
      case 8:
        return formData.turnipPrice >= 0
      // Description
      case 9:
        return !!formData.description.trim().length
      // Island limit
      case 10:
        return (
          formData.islandLimit >= 1 &&
          formData.islandLimit <= 11 &&
          formData.queueLimit >= 1 &&
          formData.queueLimit <= 100
        )
      // Entry fee
      case 11:
        return true
      // Privacy
      case 12:
        return true
      // Patreon Benefits
      case 13:
        if (!hasPatreonTier(1)) return true
        return (
          !isCheckingUrlCode.value &&
          (formData.customUrlTag === '' || urlCodeAvailable.value) &&
          CUSTOM_CODE_VALID.test(formData.customUrlTag)
        )
      default:
        return true
    }
  })

  watch(
    () => formData.dodoCode,
    (newVal, oldVal) => {
      if (!DODO_DIGITS_VALID.test(newVal)) formData.dodoCode = oldVal
    },
  )

  watchDebounced(
    () => formData.customUrlTag,
    () => {
      checkUrlCode()
    },
    { debounce: 1000 },
  )

  const onLastStep = computed(() => currentStep.value === stepCount.value - 1)

  watchEffect(() => {
    if (!canHostIsland.value) return
    if (!closedIslands.value?.length && currentStep.value == 0 && !!user && !isAnonymous.value) {
      currentStep.value = 1
      return
    }

    if (!closedIslands.value) return
    const { value: islands } = closedIslands
    const [firstIsland] = islands

    if (currentStep.value == 0 && !chosenIslandId.value && firstIsland)
      chosenIslandId.value = firstIsland.islandId
  })

  whenever(Enter!, () => {
    if (!canProceed.value) return
    nextStep()
  })

  onMounted(() => {
    rootEl.value?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
  })
</script>

<template>
  <DefineHeader v-slot="{ $slots }">
    <h2 class="font-display mb-2 text-center text-3xl font-bold">
      <component :is="$slots.default" />
    </h2>
  </DefineHeader>

  <DefineStepIndicator>
    <div class="mb-8 flex justify-center gap-1">
      <span
        v-for="i in stepCount"
        :key="i"
        class="h-1 w-1 rounded-full bg-green-400"
        :class="i <= stepIndication ? 'opacity-100' : 'opacity-50'"
      ></span>
    </div>
  </DefineStepIndicator>

  <DefineStep v-slot="{ $slots, hideIndicator }">
    <div class="flex h-full flex-col">
      <Header>
        <component :is="$slots.header" />
      </Header>
      <StepIndicator v-if="!hideIndicator" />
      <div class="mb-8 flex h-80 items-center justify-center lg:h-60">
        <component :is="$slots.default" />
      </div>
    </div>
  </DefineStep>

  <div
    class="w-full max-w-2xl rounded-xl border border-green-400/80 bg-purple-500 p-8 text-white shadow-2xl"
    ref="rootEl"
  >
    <!-- Step 0: Not Logged In / Anonymous -->
    <Step v-show="currentStep === 0" v-if="!user || isAnonymous">
      <template #header> Registered Account Required </template>
      <template #default>
        <div class="mt-10 flex w-full flex-col items-center justify-center">
          <div class="flex flex-col flex-nowrap items-stretch justify-center gap-3 text-center">
            <div class="flex flex-col">
              <p class="text-lg">
                You must be logged into Turnip Exchange with a registered account to host an Island
              </p>
            </div>
          </div>
        </div>
      </template>
    </Step>
    <!-- Step 0: Concurrent Limit Hit -->
    <Step v-show="currentStep === 0" v-else-if="!canHostIsland">
      <template #header> Island Limit Reached </template>
      <template #default>
        <div class="mt-10 flex w-full flex-col items-center justify-center">
          <div class="flex flex-col flex-nowrap items-stretch justify-center gap-3 text-center">
            <div class="flex flex-col">
              <p>You are currently actively hosting the maximum number of islands.</p>
              <p>Please close a hosted island before hosting another:</p>
            </div>

            <ul>
              <li v-for="activeIsland in activeIslands" :key="activeIsland.islandId">
                <a
                  class="text-base-tan hover:text-base-white cursor-pointer"
                  :href="`/island/${activeIsland.turnipCode}`"
                  >{{ activeIsland.name }}</a
                >
              </li>
            </ul>
          </div>
        </div>
      </template>
    </Step>
    <!-- Step 0: Rehost Existing -->
    <Step v-show="currentStep === 0" v-if="canHostIsland && closedIslands?.length">
      <template #header> Re-host an existing Island? </template>
      <template #default>
        <div class="mt-10 flex w-full flex-col items-center justify-center">
          <div class="flex flex-col flex-nowrap items-stretch justify-center">
            <TurnipExchangeDropdown
              v-model="chosenIslandId"
              :items="islandDropdownList"
              :label="chosenIslandItem?.label"
              :icon="TurnipIcon"
            >
            </TurnipExchangeDropdown>

            <IslandCard
              v-if="chosenIsland"
              :preview="true"
              :island="chosenIsland"
              class="-top-8 -mx-15 w-120 scale-75"
            />
          </div>
        </div>
      </template>
    </Step>

    <!-- Step 1: Island Name -->
    <Step v-show="currentStep === 1">
      <template #header> What is your Island's name? </template>
      <template #default>
        <div class="flex w-full justify-center">
          <input
            v-model="formData.name"
            type="text"
            maxlength="10"
            class="w-4/5 self-center rounded-lg border-2 border-white/20 bg-transparent px-4 py-3 text-center text-2xl font-bold focus:border-green-400 focus:outline-none lg:w-1/2"
            placeholder="Island Name"
          />
        </div>
      </template>
    </Step>

    <!-- Step 2: Dodo Code -->
    <Step v-show="currentStep === 2">
      <template #header> What is your Dodo Code? </template>
      <template #default>
        <div class="flex w-full justify-center">
          <input
            v-model="formData.dodoCode"
            maxlength="5"
            type="text"
            class="w-1/2 self-center rounded-lg border-2 border-white/20 bg-transparent px-4 py-3 text-center text-2xl font-bold uppercase focus:border-green-400 focus:outline-none"
            placeholder="A1B2C"
          />
        </div>
      </template>
    </Step>

    <!-- Step 3: Island Type -->
    <Step v-show="currentStep === 3">
      <template #header> What type of Island do you want? </template>
      <template #default>
        <div class="grid w-full grid-cols-2 gap-4">
          <button
            @click="formData.type = 'turnips'"
            :class="[
              'col-span-2 cursor-pointer rounded-lg border-2 py-2 font-bold transition-all',
              formData.type === 'turnips'
                ? 'border-green-400 bg-green-950'
                : 'border-white/20 bg-white/10 hover:border-white/40',
            ]"
          >
            Turnips
          </button>
          <button
            v-for="type in islandTypes.slice(1)"
            :key="type.value"
            @click="formData.type = type.value"
            :class="[
              'cursor-pointer rounded-lg border-2 py-2 font-bold transition-all',
              formData.type === type.value
                ? 'border-green-400 bg-green-950'
                : 'border-white/20 bg-white/10 hover:border-white/40',
            ]"
          >
            {{ type.name }}
          </button>
        </div>
      </template>
    </Step>

    <!-- Step 4: Fruit -->
    <Step v-show="currentStep === 4">
      <template #header> What is Your Island's Fruit? </template>
      <template #default>
        <div class="grid grid-cols-3 gap-4 lg:grid-cols-6">
          <button
            v-for="fruit in fruits"
            :key="fruit.value"
            @click="formData.fruit = fruit.value"
            :class="[
              'cursor-pointer rounded-xl border-2 p-4 transition-all',
              formData.fruit === fruit.value
                ? 'border-green-400 bg-green-950'
                : 'border-white/20 bg-white/10 hover:border-white/40',
            ]"
          >
            <img :src="fruit.icon" :alt="fruit.name" class="h-12 w-12" />
          </button>
        </div>
      </template>
    </Step>

    <!-- Step 5: Hemisphere -->
    <Step v-show="currentStep === 5">
      <template #header> Which Hemisphere is your Island in? </template>
      <template #default>
        <div class="grid w-full grid-cols-2 gap-4">
          <button
            @click="formData.hemisphere = 'north'"
            :class="[
              'cursor-pointer rounded-lg border-2 py-8 text-xl font-bold transition-all',
              formData.hemisphere === 'north'
                ? 'border-green-400 bg-green-950'
                : 'border-white/20 bg-white/10 hover:border-white/40',
            ]"
          >
            North
          </button>
          <button
            @click="formData.hemisphere = 'south'"
            :class="[
              'cursor-pointer rounded-lg border-2 py-8 text-xl font-bold transition-all',
              formData.hemisphere === 'south'
                ? 'border-green-400 bg-green-950'
                : 'border-white/20 bg-white/10 hover:border-white/40',
            ]"
          >
            South
          </button>
        </div>
      </template>
    </Step>

    <!-- Step 6: Time -->
    <Step v-show="currentStep === 6">
      <template #header> What Time is it on your Island? </template>
      <template #default>
        <input
          v-model="islandTime"
          type="datetime-local"
          class="self-center rounded-lg border-2 border-white/20 bg-white/10 px-4 py-3 text-white focus:border-green-400 focus:outline-none"
        />
      </template>
    </Step>

    <!-- Step 7: NPC -->
    <Step v-show="currentStep === 7">
      <template #header> Who is on your Island? </template>
      <template #default>
        <div class="grid grid-cols-4 gap-4">
          <button
            v-for="npc in npcs"
            :key="npc.value"
            @click="toggleNpc(npc.value)"
            :class="[
              'flex h-16 w-16 cursor-pointer items-center justify-center rounded-xl border-2 transition-all md:h-20 md:w-20',
              formData.npc.includes(npc.value)
                ? 'border-green-400 bg-green-950'
                : 'border-white/20 bg-white/10 hover:border-white/40',
            ]"
          >
            <img :src="npc.icon" :alt="npc.name" class="h-12 w-12 md:h-16 md:w-16" />
          </button>
        </div>
      </template>
    </Step>

    <!-- Step 8: Turnip Price -->
    <Step v-show="currentStep === 8">
      <template #header> What is your Turnip Price? </template>
      <template #default>
        <div class="flex w-full justify-center">
          <ClampedIntegerInput
            v-model.number="formData.turnipPrice"
            type="number"
            :min="0"
            :max="999999999"
            class="w-1/3 self-center rounded-lg border-2 border-white/20 bg-transparent px-4 py-3 text-center text-2xl font-bold focus:border-green-400 focus:outline-none"
            placeholder="0"
          />
        </div>
      </template>
    </Step>

    <!-- Step 9: Description -->
    <Step v-show="currentStep === 9">
      <template #header> Tell Us More! </template>
      <template #default>
        <textarea
          v-model="formData.description"
          maxlength="2048"
          class="h-32 w-full resize-none rounded-lg border-2 border-white/20 bg-white/5 p-4 text-white focus:border-green-400 focus:outline-none"
          placeholder="Write a message for your visitors..."
        ></textarea>
      </template>
    </Step>

    <!-- Step 10: Visitors -->
    <Step v-show="currentStep === 10">
      <template #header> How many visitors can join? </template>
      <template #default>
        <div class="flex flex-col gap-y-4">
          <div class="flex justify-center gap-8">
            <div class="flex flex-col gap-2">
              <label class="text-sm font-bold opacity-80">Island Limit</label>
              <ClampedIntegerInput
                v-model.number="formData.islandLimit"
                :min="0"
                :max="11"
                class="w-full rounded-lg border-2 border-white/20 bg-transparent px-4 py-3 text-center text-2xl font-bold focus:border-green-400 focus:outline-none"
                placeholder="1"
              />
            </div>
            <div class="flex flex-col gap-2">
              <label class="text-sm font-bold opacity-80">Queue Limit</label>
              <ClampedIntegerInput
                v-model.number="formData.queueLimit"
                :min="0"
                :max="100"
                class="w-full rounded-lg border-2 border-white/20 bg-transparent px-4 py-3 text-center text-2xl font-bold focus:border-green-400 focus:outline-none"
                placeholder="5"
              />
            </div>
          </div>
          <div class="flex flex-col justify-center gap-y-1">
            <p class="text-left text-xs">
              You will allow <b>{{ formData.islandLimit }}</b> visitors on your island at the same
              time.
            </p>
            <p class="text-left text-xs">
              You will have a max of <b>{{ formData.queueLimit }}</b> visitors in the queue.
            </p>
          </div>
        </div>
      </template>
    </Step>

    <!-- Step 11: Entry Fee -->
    <Step v-show="currentStep === 11">
      <template #header> Want to charge an Entry Fee? </template>
      <template #default>
        <div class="flex flex-col gap-y-4">
          <div class="flex w-full flex-row justify-center gap-4">
            <div
              :class="[
                'flex h-32 w-32 flex-col items-center justify-center rounded-xl border-2 transition-all',
                entryFeeData.bells > 0
                  ? 'border-green-400 bg-green-950'
                  : 'border-white/20 bg-white/10 hover:border-white/40',
              ]"
            >
              <img src="/images/icons/bells.svg" alt="Bells" class="mb-2 h-16 w-16" />
              <ClampedIntegerInput
                v-model.number="entryFeeData.bells"
                type="number"
                :min="0"
                :max="99999999"
                class="text-md w-28 rounded-lg border-2 border-white/20 bg-transparent px-1 py-1 text-center font-bold focus:border-green-400 focus:outline-none"
                placeholder="0"
              />
            </div>
            <div
              :class="[
                'flex h-32 w-32 flex-col items-center justify-center rounded-xl border-2 transition-all',
                entryFeeData.nmt > 0
                  ? 'border-green-400 bg-green-950'
                  : 'border-white/20 bg-white/10 hover:border-white/40',
              ]"
            >
              <img src="/images/icons/nmt.svg" alt="NMTs" class="mb-2 h-16 w-16" />
              <ClampedIntegerInput
                v-model.number="entryFeeData.nmt"
                :min="0"
                :max="9999"
                class="text-md w-28 rounded-lg border-2 border-white/20 bg-transparent px-1 py-1 text-center font-bold focus:border-green-400 focus:outline-none"
                placeholder="0"
              />
            </div>
          </div>
          <div class="w-full justify-center gap-4 text-sm">
            <span
              >Visitors must pay <span v-html="feeDescription"></span> upon arrival to your
              island</span
            >
          </div>
        </div>
      </template>
    </Step>

    <!-- Step 12: Privacy -->
    <Step v-show="currentStep === 12">
      <template #header> Need any Privacy? </template>
      <template #default>
        <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <button
            @click="formData.private = !formData.private"
            :class="[
              'flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 transition-all',
              formData.private
                ? 'border-green-400 bg-green-950'
                : 'border-white/20 bg-white/10 hover:border-white/40',
            ]"
          >
            <span class="text-3xl">
              <i-fa7-solid-eye-slash />
            </span>
            <span class="text-xs font-bold">Private</span>
          </button>
          <button
            v-for="opt in privacyOptions"
            :key="opt.value"
            @click="togglePrivacy(opt.value)"
            :class="[
              'flex h-24 w-24 cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 transition-all',
              formData.privacy.includes(opt.value)
                ? 'border-green-400 bg-green-950'
                : 'border-white/20 bg-white/10 hover:border-white/40',
            ]"
          >
            <!-- Icons placeholders since actual mdi icons might need importing or resoling -->
            <span class="text-3xl">
              <i-fa7-brands-twitch v-if="opt.value === 'twitch-sub'" />
              <i-fa7-brands-discord v-else-if="opt.value === 'discord'" />
              <i-fa7-brands-patreon v-else-if="opt.value === 'patreon'" />
            </span>
            <span class="text-xs font-bold">{{ opt.name }}</span>
          </button>
        </div>
      </template>
    </Step>

    <!-- Step 13?: Patreon Benefits -->
    <Step v-show="hasPatreonTier(1) && currentStep === 13">
      <template #header> Patreon Benefits </template>
      <template #default>
        <div class="flex w-full flex-col items-center justify-center gap-2">
          <label>Custom Island URL</label>
          <div class="mb-3 flex w-full justify-center">
            <input
              v-model="formData.customUrlTag"
              type="text"
              maxlength="20"
              class="w-4/5 self-center rounded-lg border-2 border-white/20 bg-transparent px-4 py-3 text-center text-2xl font-bold focus:border-green-400 focus:outline-none lg:w-1/2"
              placeholder="Custom URL"
            />
          </div>
          <p class="text-xs" v-show="isCheckingUrlCode">Checking URL...</p>
          <p
            class="text-xs"
            v-show="
              !isCheckingUrlCode &&
              !urlCodeAvailable &&
              CUSTOM_CODE_VALID.test(formData.customUrlTag) &&
              formData.customUrlTag !== ''
            "
          >
            URL Tag is unavailable
          </p>
          <p
            class="text-xs"
            v-show="
              !isCheckingUrlCode &&
              urlCodeAvailable &&
              CUSTOM_CODE_VALID.test(formData.customUrlTag) &&
              formData.customUrlTag !== ''
            "
          >
            URL Tag is available!
          </p>
          <p
            class="text-xs"
            v-show="
              !isCheckingUrlCode &&
              !CUSTOM_CODE_VALID.test(formData.customUrlTag) &&
              formData.customUrlTag !== ''
            "
          >
            Tag invalid! Please use lowercase letters, digits, hyphens and underscores.
          </p>

          <label>Custom Background</label>
          <div class="grid grid-cols-4 gap-4 lg:grid-cols-8">
            <button
              v-for="bg in backgrounds"
              :key="bg.value"
              @click="formData.customBackgroundId = bg.value"
              :class="[
                backgroundMap[bg.value] ?? '',
                'cursor-pointer rounded-xl border-2 transition-all',
                formData.customBackgroundId === bg.value
                  ? 'border-green-400 bg-green-950'
                  : 'border-white/20 hover:border-white/40',
              ]"
            >
              <p class="h-11 w-11"></p>
            </button>
          </div>
        </div>
      </template>
    </Step>

    <!-- Step 13/14: Summary -->
    <Step v-show="onLastStep" :hideIndicator="true">
      <template #header> Ready to Host? </template>
      <template #default>
        <div class="flex w-full flex-col lg:flex-row">
          <IslandCard
            :preview="true"
            :island="previewIslandData"
            class="-mx-10 w-[120%] scale-75 lg:w-120"
          />
          <div class="-mt-5 flex w-full flex-1 gap-2 text-sm lg:mt-10 lg:w-40 lg:justify-end">
            <dl
              class="mx-2 grid w-full list-none auto-rows-auto grid-cols-[50%_50%] items-start lg:mx-auto lg:w-auto lg:grid-cols-1"
            >
              <dt class="text-left text-sm lg:text-center lg:text-lg">Dodo Code:</dt>
              <dd class="mb-0 text-right uppercase lg:mb-3 lg:text-center">
                {{ previewIslandData.dodoCode }}
              </dd>
              <dt class="text-left text-sm lg:text-center lg:text-lg">Entry Fee:</dt>
              <dd class="mb-0 text-right lg:mb-3 lg:text-center">
                <span v-html="feeDescription"></span>
              </dd>
              <dt class="text-left text-sm lg:text-center lg:text-lg">Privacy:</dt>
              <dd class="mb-0 text-right lg:mb-3 lg:text-center">
                <span v-if="previewIslandData.private" class="mr-2">
                  <i-fa7-solid-eye-slash class="inline" />
                </span>
                <span v-if="previewIslandData.privacy.includes('twitch-sub')" class="mx-2">
                  <i-fa7-brands-twitch class="inline" />
                </span>
                <span v-if="previewIslandData.privacy.includes('discord')" class="mx-2">
                  <i-fa7-brands-discord class="inline" />
                </span>
                <span v-if="previewIslandData.privacy.includes('patreon')" class="ml-2">
                  <i-fa7-brands-patreon class="inline" />
                </span>
                <span v-if="previewIslandData.privacy.length == 0 && !previewIslandData.private">
                  None
                </span>
              </dd>
            </dl>
          </div>
        </div>
      </template>
    </Step>

    <!-- Navigation -->
    <div v-if="currentStep > 0" class="flex justify-center gap-4">
      <button
        v-if="currentStep > (closedIslands?.length ? 0 : 1)"
        @click="prevStep"
        class="cursor-pointer font-bold text-green-400 transition-colors hover:text-green-300"
      >
        Back
      </button>

      <button
        @click="onLastStep ? submit() : nextStep()"
        :class="[
          'cursor-pointer rounded-lg bg-green-400 px-6 py-2 font-bold text-black transition-colors hover:bg-green-500',
          canProceed ? '' : 'opacity-50',
        ]"
        :disabled="!canProceed"
      >
        {{ onLastStep ? 'Host Island' : 'Next' }}
      </button>
    </div>
    <div v-else-if="!canHostIsland || !user || isAnonymous" class="flex justify-center gap-4">
      <button
        @click="onLeaveWizardClicked"
        class="cursor-pointer rounded-lg bg-green-400 px-6 py-2 font-bold text-black transition-colors hover:bg-green-500"
      >
        Leave
      </button>
    </div>
    <div v-else class="flex justify-center gap-4">
      <button
        @click="onHostIslandClicked"
        class="cursor-pointer rounded-lg bg-green-400 px-6 py-2 font-bold text-black transition-colors hover:bg-green-500"
      >
        Host
      </button>

      <button
        @click="onHostEditClicked"
        class="cursor-pointer rounded-lg bg-green-400 px-6 py-2 font-bold text-black transition-colors hover:bg-green-500"
      >
        Edit
      </button>

      <button
        @click="onHostNewClicked"
        class="cursor-pointer rounded-lg bg-green-400 px-6 py-2 font-bold text-black transition-colors hover:bg-green-500"
      >
        New
      </button>
    </div>
  </div>
</template>

<style scoped>
  dt {
    font-weight: bold;
  }
</style>
